import { organizationContent } from "@/features/organizations/slice";
import { useAppSelector, useOrder } from "@/hooks";
import { Content, OrderItem } from "@/types";
import { Button, CircularProgress, Stack, Typography } from "@mui/material"
import { useMemo, useState } from "react";
import CardMedia from '@mui/material/CardMedia';
import Price from "@/components/Price";
import { getOrderItemPrice } from "@/utils/order";
import { sanitizeNumber } from "@/utils/numbers";
import { v4 } from "uuid";
import { Add01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useLazyFetchContentSettingsQuery } from "@/features/organizations/api/settingsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import RelatedDishDrawer from "./RelatedDishDrawer";
import { getThumbnailImage } from "@/utils/media";

interface Props {
    dish: Content;
}

const RelatedProducts = ({ dish }: Props) => {
    const { addItems } = useOrder();
    const organizationId = useAppSelector(store => store?.ui?.activeOrganization);
    const activeBranch = useAppSelector((store) => store.ui.activeBranch);
    const content = useAppSelector(organizationContent(organizationId)) as Content[];
    const [relatedDish, setRelatedDish] = useState<Content | null>(null);
    const [fetchSettings, fetchSettingsQuery] = useLazyFetchContentSettingsQuery();

    const relatedDishes = useMemo(() => {
        if (!dish?.extras || !content?.length) {
            return [];
        }

        return dish.extras.map(dishId => {
            return content.find(c => c.id === dishId);
        }) as Content[]
    }, [content])

    const addItemToOrder = async (dishIndex: number) => {
        const dishDraft = relatedDishes?.[dishIndex];

        if (!dishDraft || !organizationId || !dishDraft?.id) {
            return;
        }

        const results = await fetchSettings({
            organizationId: organizationId ?? skipToken,
            contentId: dishDraft?.id ?? skipToken,
        })

        if (!results.data?.length) {
            await addItems([{ ...dishDraft, quantity: 1, itemId: v4() } as unknown as OrderItem]);
        } else {
            setRelatedDish({ ...dishDraft, settings: results.data });
        }
    }

    const handleRelatedDishClose = () => {
        setRelatedDish(null);
    }

    if (!dish?.extras || !content?.length) {
        return null;
    }

    return (
        <>
            <Stack spacing={2}>
                <Typography variant="overline">Sugeridos</Typography>
                <Stack spacing={1.5}>
                    {relatedDishes.map((dish, dishIndex) => (
                        <Stack key={dish.id} direction="row" spacing={2} alignItems="center">
                            <CardMedia
                                component="img"
                                image={getThumbnailImage({ organizationId, file: dish?.images, size: '150x150' })}
                                alt={dish.title}
                                sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: 1.5,
                                }}
                            />
                            <Stack flex={1}>
                                <Typography variant="body1" fontWeight={500} color="textPrimary">{dish.title}</Typography>
                                <Typography variant="body1">
                                    <Price currency="NIO">{dish.price != 0 ? getOrderItemPrice(dish as unknown as OrderItem) : sanitizeNumber(dish?.startingPrice)}</Price>
                                </Typography>
                            </Stack>
                            {fetchSettingsQuery.originalArgs?.contentId === dish.id && fetchSettingsQuery.isLoading ? (
                                <CircularProgress size={24} />
                            ) : (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<HugeiconsIcon icon={Add01Icon} size={14} />}
                                    onClick={() => addItemToOrder(dishIndex)}
                                    disabled={dish?.isDisabled || dish?.branchSettings?.[activeBranch]?.isDisabled}
                                >
                                    Añadir
                                </Button>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Stack>
            <RelatedDishDrawer dish={relatedDish} isOpen={Boolean(relatedDish)} onClose={handleRelatedDishClose} />
        </>
    )
}

export default RelatedProducts;
